import React, { useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { PageHeader } from './Services';
import { Phone, Mail, MapPin, Clock, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { company, services } from '../mock/mock';
import PageMeta from '../components/PageMeta';
import { localBusinessLd, buildBreadcrumbLd } from '../lib/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '', agency: '', email: '', phone: '', service: '', branches: '1-3', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: 'Almost there', description: 'Please add your name and email so we can reply.' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form, { timeout: 20000 });
      setSubmitted(true);
      toast({ title: 'Thanks — we have you', description: 'A senior account manager will be in touch within one working hour.' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong sending your enquiry. Please call us on 03333 445 336 and we will pick it up.';
      toast({ title: 'Could not send enquiry', description: detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageMeta
        title="Contact · Residential Support — UK lettings outsourcing partner"
        description="Call 03333 445 336 or message our team. We will reply quickly with a tailored setup for your branch — UK letting agents only."
        path="/contact"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(localBusinessLd)}</script>
        <script type="application/ld+json">
          {JSON.stringify(
            buildBreadcrumbLd([
              { label: 'Home', path: '/' },
              { label: 'Contact', path: '/contact' },
            ])
          )}
        </script>
      </Helmet>
      <PageHeader
        eyebrow="Contact"
        title="Tell us about your branch — we will reply within the hour."
        sub="Whether you have one branch or fifty, our team will scope a setup that fits. No obligation, no pressure."
      />

      <section className="sect bg-[var(--rs-cream)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="rs-card p-8">
              <h3 className="h-display text-2xl text-[var(--rs-ink)]">Talk to a human</h3>
              <p className="text-[var(--rs-ink)]/65 text-sm mt-2">No bots, no call centre. A real UK-based account manager, every weekday.</p>

              <div className="mt-7 space-y-5 text-sm">
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-lg bg-[var(--rs-green)]/10 text-[var(--rs-green)] grid place-items-center"><Phone className="w-4 h-4" /></span>
                  <div><div className="text-[var(--rs-ink)]/55 text-xs uppercase tracking-widest">Call</div><a href={`tel:${company.phone}`} className="text-[var(--rs-ink)] font-medium">{company.phone}</a></div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-lg bg-[var(--rs-green)]/10 text-[var(--rs-green)] grid place-items-center"><Mail className="w-4 h-4" /></span>
                  <div><div className="text-[var(--rs-ink)]/55 text-xs uppercase tracking-widest">Email</div><a href={`mailto:${company.email}`} className="text-[var(--rs-ink)] font-medium">{company.email}</a></div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-lg bg-[var(--rs-green)]/10 text-[var(--rs-green)] grid place-items-center"><MapPin className="w-4 h-4" /></span>
                  <div><div className="text-[var(--rs-ink)]/55 text-xs uppercase tracking-widest">Head office</div><div className="text-[var(--rs-ink)]">{company.address}</div></div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-lg bg-[var(--rs-green)]/10 text-[var(--rs-green)] grid place-items-center"><Clock className="w-4 h-4" /></span>
                  <div><div className="text-[var(--rs-ink)]/55 text-xs uppercase tracking-widest">Hours</div><div className="text-[var(--rs-ink)]">{company.hours}</div></div>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-[20px] overflow-hidden border border-[#E7E0D2]">
              <iframe
                title="Office map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-1.1300%2C52.6090%2C-1.1090%2C52.6240&layer=mapnik&marker=52.6165%2C-1.1195"
                className="w-full h-[260px] border-0"
                loading="lazy"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rs-card p-8 lg:p-10">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[var(--rs-green)]/10 text-[var(--rs-green)] grid place-items-center"><CheckCircle2 className="w-8 h-8" /></div>
                  <h3 className="h-display text-3xl mt-5 text-[var(--rs-ink)]">Thanks {form.name.split(' ')[0]} — we have you.</h3>
                  <p className="text-[var(--rs-ink)]/65 mt-3 max-w-md mx-auto">A senior account manager will be in touch within one working hour. In the meantime, you can read how it all works.</p>
                  <button onClick={() => { setSubmitted(false); setForm({ name:'', agency:'', email:'', phone:'', service:'', branches:'1-3', message:'' }); }} className="btn-ghost mt-7">Send another</button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-[var(--rs-ink)]">Full name *</Label>
                    <Input value={form.name} onChange={handle('name')} placeholder="Your full name" className="mt-1.5 bg-white border-[#E7E0D2] focus-visible:ring-[var(--rs-green)]" />
                  </div>
                  <div>
                    <Label className="text-[var(--rs-ink)]">Agency name</Label>
                    <Input value={form.agency} onChange={handle('agency')} placeholder="Your agency" className="mt-1.5 bg-white border-[#E7E0D2]" />
                  </div>
                  <div>
                    <Label className="text-[var(--rs-ink)]">Work email *</Label>
                    <Input type="email" value={form.email} onChange={handle('email')} placeholder="you@agency.co.uk" className="mt-1.5 bg-white border-[#E7E0D2]" />
                  </div>
                  <div>
                    <Label className="text-[var(--rs-ink)]">Phone</Label>
                    <Input value={form.phone} onChange={handle('phone')} placeholder="Best contact number" className="mt-1.5 bg-white border-[#E7E0D2]" />
                  </div>
                  <div>
                    <Label className="text-[var(--rs-ink)]">Service of interest</Label>
                    <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                      <SelectTrigger className="mt-1.5 bg-white border-[#E7E0D2]"><SelectValue placeholder="Pick a service" /></SelectTrigger>
                      <SelectContent>
                        {services.map((s) => (<SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>))}
                        <SelectItem value="multi">All of the above</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[var(--rs-ink)]">Branches</Label>
                    <Select value={form.branches} onValueChange={(v) => setForm({ ...form, branches: v })}>
                      <SelectTrigger className="mt-1.5 bg-white border-[#E7E0D2]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 branch</SelectItem>
                        <SelectItem value="1-3">2 – 3 branches</SelectItem>
                        <SelectItem value="4-10">4 – 10 branches</SelectItem>
                        <SelectItem value="10+">10+ branches</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[var(--rs-ink)]">How can we help?</Label>
                    <Textarea rows={5} value={form.message} onChange={handle('message')} placeholder="Tell us about your branch — volumes, postcodes, current pain points…" className="mt-1.5 bg-white border-[#E7E0D2]" />
                  </div>
                  <div className="md:col-span-2 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <p className="text-xs text-[var(--rs-ink)]/55 max-w-sm">By submitting you agree to our privacy policy. We never share your details with third parties.</p>
                    <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed">
                      {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : (<>Send enquiry <ArrowRight className="w-4 h-4" /></>)}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      <Toaster />
    </div>
  );
};

export default Contact;
